/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

// Display-side mirror of the backend AddOnPricing.ComputeAmount. The server recomputes the authoritative
// charge; this is the estimate shown on the booking page. Day-granular storefront → hour/minute approximated.

export type PricingType =
  | 'per_minute' | 'per_hour' | 'per_day' | 'per_week' | 'per_month' | 'percent' | 'one_time';

/** Amount for one add-on line over a rental of `days` days. `rentalBase` is used only for the percent cadence. */
export function addOnLineAmount(
  pricingType: string,
  price: number,
  quantity: number,
  days: number,
  rentalBase = 0,
): number {
  const qty = quantity < 1 ? 1 : quantity;
  const d = Math.max(1, days || 1);
  const p = Number(price) || 0;
  switch (pricingType) {
    case 'one_time':   return p * qty;
    case 'per_minute': return p * qty * Math.max(1, Math.ceil(d * 24 * 60));
    case 'per_hour':   return p * qty * Math.max(1, Math.ceil(d * 24));
    case 'per_week':   return p * qty * Math.max(1, Math.ceil(d / 7));
    case 'per_month':  return p * qty * Math.max(1, Math.ceil(d / 30));
    case 'percent':    return Math.round(rentalBase * (p / 100) * 100) / 100;
    case 'per_day':
    default:           return p * qty * d;
  }
}

/** Short cadence suffix for the booking line (e.g. "/ day", "(one-time)", "% of rental").
 * `t` accepts the react-i18next translate fn (key, defaultValue) → string. */
export type TranslateFn = (key: string, defaultValue?: string) => string;
export function cadenceSuffix(pricingType: string, t: TranslateFn): string {
  switch (pricingType) {
    case 'one_time':   return t('booking.oneTime', '(one-time)');
    case 'per_minute': return `/ ${t('units.minute', 'min')}`;
    case 'per_hour':   return `/ ${t('units.hour', 'hour')}`;
    case 'per_week':   return `/ ${t('units.week', 'week')}`;
    case 'per_month':  return `/ ${t('units.month', 'month')}`;
    case 'percent':    return t('booking.percentOfRental', '% of rental');
    case 'per_day':
    default:           return `/ ${t('units.day', 'day')}`;
  }
}

/** Plain-text version of an add-on description: imported HQ descriptions carry HTML (e.g. "<p>…</p>"),
 * and the translator preserves it. Strip tags + decode the few common entities so the storefront shows
 * clean text instead of literal markup. No DOM injection (XSS-safe). */
export function stripHtml(s?: string | null): string {
  if (!s) return '';
  return s
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}
